import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import PostAction from "../store/post/action";
import FavoritesActions from "../store/favorites/action";
import ProductCard from "../components/ProductCard";
import Clothes from "../models/clothes/clothes.model";
import { User } from "../models/auth/auth.model";
import { Collections, CollectionsClass } from "../utils/firestore/collections";
import { handleErrors } from "../utils/firestore/handleErrors";

const SellerProfile = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const sellerFromState = location.state?.seller as User | undefined;
    const postsFromState = location.state?.sellersPosts as Clothes[] | undefined;
    const { user } = useAppSelector((s) => s.auth);
    const { seller, sellersPosts, isLoading } = useAppSelector((s) => s.post);
    const { favorites } = useAppSelector((s) => s.favorites);

    const profile = seller ?? sellerFromState;
    const posts = (sellersPosts && sellersPosts.length > 0) ? sellersPosts : (postsFromState ?? []);

    const [reportTitle, setReportTitle] = useState("");
    const [reportMessage, setReportMessage] = useState("");
    const [reportSending, setReportSending] = useState(false);
    const [reportError, setReportError] = useState("");
    const [reportSuccess, setReportSuccess] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [showLoginToContactModal, setShowLoginToContactModal] = useState(false);

    const onContactClick = (link: string) => {
        if (!user) {
            setShowLoginToContactModal(true);
            return;
        }
        if (link.startsWith("wa:")) {
            const num = link.slice(3).replace(/\D/g, "");
            window.open(`https://wa.me/${num}`, "_blank");
        } else {
            window.open(link, "_blank");
        }
    };

    useEffect(() => {
        if (id && !sellerFromState) {
            PostAction.getSellerInfo(id, dispatch);
            PostAction.getSellersPosts(id, dispatch);
        }
        return () => {
            PostAction.clearSeller(dispatch);
        };
    }, [id, dispatch, sellerFromState]);

    const onItemClick = (item: Clothes) => {
        navigate("/item", { state: { item } });
    };

    const onToggleFavorite = (item: Clothes, e: React.MouseEvent) => {
        e.stopPropagation();
        if (FavoritesActions.isFavorite(item, favorites)) {
            FavoritesActions.removeItem(item, favorites, dispatch);
        } else {
            FavoritesActions.addItem(item, favorites, dispatch);
        }
    };

    const handleSubmitReport = (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile || !user?.email) return;
        setReportError("");
        setReportSuccess(false);
        const trimmedEmail = user.email.trim().toLowerCase();
        const trimmedTitle = reportTitle.trim();
        const trimmedMessage = reportMessage.trim();
        if (!trimmedTitle) {
            setReportError("Titulli është i detyrueshëm.");
            return;
        }
        if (!trimmedMessage) {
            setReportError("Mesazhi është i detyrueshëm.");
            return;
        }
        setReportSending(true);
        const coll = collection(CollectionsClass.instance, Collections.SELLER_REPORTS);
        const pendingQuery = query(
            coll,
            where("email", "==", trimmedEmail),
            where("seller_id", "==", profile.id),
            where("status", "==", "pending")
        );
        getDocs(pendingQuery)
            .then((snap) => {
                if (!snap.empty) {
                    setReportError("Keni një raport në pritje për këtë shitës. Ju lutemi prisni përgjigjen tonë para se të dërgoni një të ri.");
                    setReportSending(false);
                    return;
                }
                return addDoc(coll, {
                    email: trimmedEmail,
                    seller_id: profile.id,
                    seller_email: profile.email ?? "",
                    title: trimmedTitle,
                    message: trimmedMessage,
                    status: "pending",
                    created_at: Date.now(),
                });
            })
            .then((ref) => {
                if (ref) {
                    setReportTitle("");
                    setReportMessage("");
                    setReportSuccess(true);
                    setReportModalOpen(false);
                    setReportError("");
                }
            })
            .catch((err) => setReportError(handleErrors(err)))
            .finally(() => setReportSending(false));
    };

    const openReportModal = () => {
        setReportError("");
        setReportSuccess(false);
        setReportModalOpen(true);
    };

    const closeReportModal = () => {
        setReportModalOpen(false);
        setReportError("");
    };

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-6xl mx-auto px-4 py-6">
                {profile && (
                    <div className="bg-white rounded-xl shadow p-6 mb-6">
                        <h1 className="text-xl font-semibold text-[#704e37]">
                            {profile.first_name} {profile.last_name}
                        </h1>
                        <div className="mt-3">
                            <p className="text-sm font-semibold text-gray-800 mb-2">Kontakti</p>
                            {(profile.contact?.facebook && profile.verifiedContacts?.facebook === "approved") ||
                            (profile.contact?.instagram && profile.verifiedContacts?.instagram === "approved") ||
                            (profile.contact?.tiktok && profile.verifiedContacts?.tiktok === "approved") ||
                            (profile.contact?.phone && profile.verifiedContacts?.phone === "approved") ? (
                                <div className="flex flex-wrap gap-3">
                                    {profile.contact?.facebook && profile.verifiedContacts?.facebook === "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => onContactClick(profile.contact!.facebook!)}
                                            className="w-10 h-10 rounded-full bg-[#1877f2]/10 text-[#1877f2] flex items-center justify-center hover:bg-[#1877f2]/20 transition"
                                            aria-label="Facebook"
                                        >
                                            <FaFacebookF className="text-xl" />
                                        </button>
                                    )}
                                    {profile.contact?.instagram && profile.verifiedContacts?.instagram === "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => onContactClick(profile.contact!.instagram!)}
                                            className="w-10 h-10 rounded-full bg-pink-500/10 text-pink-600 flex items-center justify-center hover:bg-pink-500/20 transition"
                                            aria-label="Instagram"
                                        >
                                            <FaInstagram className="text-xl" />
                                        </button>
                                    )}
                                    {profile.contact?.tiktok && profile.verifiedContacts?.tiktok === "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => onContactClick(profile.contact!.tiktok!)}
                                            className="w-10 h-10 rounded-full bg-gray-900/10 text-gray-800 flex items-center justify-center hover:bg-gray-900/20 transition"
                                            aria-label="TikTok"
                                        >
                                            <FaTiktok className="text-xl" />
                                        </button>
                                    )}
                                    {profile.contact?.phone && profile.verifiedContacts?.phone === "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => onContactClick("wa:" + (profile.contact!.phone ?? ""))}
                                            className="w-10 h-10 rounded-full bg-green-500/10 text-green-600 flex items-center justify-center hover:bg-green-500/20 transition"
                                            aria-label="WhatsApp"
                                        >
                                            <FaWhatsapp className="text-xl" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-sm">Nuk ka kontakte të verifikuara për të shfaqur.</p>
                            )}
                        </div>
                    </div>
                )}

                {profile && user && user.id !== profile.id && (
                    <div className="mb-6 rounded-xl border border-amber-200/80 bg-amber-50/60 p-4">
                        <p className="text-gray-700 text-sm mb-2">
                            Raportoni vetëm nëse ky shitës ka shkelur rregullat e aplikacionit (p.sh. sjellje e papërshtatshme, mashtrim, përmbajtje e ndaluar). Raporti shkon te ekipi ynë për shqyrtim.
                        </p>
                        <p className="text-gray-600 text-sm mb-4">
                            Raporte të rreme ose të përsëritura pa bazë mund të çojnë në masa ndëshkuese ndaj llogarisë suaj, përfshirë pezullim ose fshirje të përhershme.
                        </p>
                        <button
                            type="button"
                            onClick={openReportModal}
                            className="px-4 py-2 rounded-lg bg-[#704e37] text-white font-medium text-sm hover:opacity-90"
                        >
                            Raporto shitësin
                        </button>
                        {reportSuccess && (
                            <p className="mt-3 text-green-600 text-sm">Raporti u dërgua. Do t'ju përgjigjemi në email.</p>
                        )}
                    </div>
                )}

                <h2 className="text-lg font-medium text-gray-700 mb-3">Shpalljet</h2>
                {isLoading && !posts.length && <p className="text-gray-500">Duke ngarkuar...</p>}
                {posts.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {posts.map((item) => (
                            <ProductCard
                                key={`${item.seller_id}_${item.id}`}
                                item={item}
                                isFavorite={FavoritesActions.isFavorite(item, favorites)}
                                onToggleFavorite={onToggleFavorite}
                                onClick={onItemClick}
                                currentUserId={user?.id}
                            />
                        ))}
                    </div>
                )}
                {!isLoading && profile && posts.length === 0 && (
                    <p className="text-gray-500">Nuk ka shpallje.</p>
                )}
            </div>

            {/* Report seller modal */}
            {showLoginToContactModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowLoginToContactModal(false)}>
                    <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Kyçuni</h3>
                        <p className="text-gray-600 text-sm mb-4">Për të kontaktuar shitësin, ju duhet të kyçeni.</p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => { setShowLoginToContactModal(false); navigate("/login"); }}
                                className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium text-sm"
                            >
                                Kyçuni
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowLoginToContactModal(false)}
                                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm"
                            >
                                Mbyll
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reportModalOpen && profile && user && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeReportModal}>
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">Raporto shitësin</h3>
                        <p className="text-gray-600 text-sm mb-2">
                            Përdorni këtë formë vetëm kur shitësi ka shkelur rregullat e aplikacionit. Raporte të pabaza mund të pasojnë me masa ndëshkuese ndaj llogarisë suaj.
                        </p>
                        <p className="text-gray-500 text-sm mb-4">
                            Raporti do të dërgohet nga <strong>{user.email}</strong>. Do t'ju përgjigjemi në këtë adresë.
                        </p>
                        {reportError && <p className="text-red-500 text-sm mb-4">{reportError}</p>}
                        <form onSubmit={handleSubmitReport} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Titulli *</label>
                                <input
                                    type="text"
                                    value={reportTitle}
                                    onChange={(e) => setReportTitle(e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                    placeholder="Titulli i raportit"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mesazhi *</label>
                                <textarea
                                    value={reportMessage}
                                    onChange={(e) => setReportMessage(e.target.value)}
                                    rows={4}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                    placeholder="Përshkruani shqetësimin..."
                                    required
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={reportSending}
                                    className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium text-sm hover:opacity-90 disabled:opacity-50"
                                >
                                    {reportSending ? "Duke dërguar..." : "Dërgo raportin"}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeReportModal}
                                    className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                                >
                                    Anulo
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SellerProfile;
